<script lang="ts">
    import { newPresentationOptions } from "../store/presentationOptions";
    import {derived} from "svelte/store";
    import {createNewPresentation} from "../actions/newPresentation";
    import {tap} from "rxjs";
    import {generateTopic} from "../actions/generateTopic";
    import { suggestions } from '../store/suggestions.js';
    import { getSuggestions } from '../actions/getSuggestions';

    const speakerOptions = derived(newPresentationOptions, (options) => options.speakers);
    const languageOptions = derived(newPresentationOptions, (options) => options.languages);
    const generatorOptions = derived(newPresentationOptions, (options) => options.generators);
    const textGeneratorOptions = derived(newPresentationOptions, (options) => options.textGenerators);
    const slideOptions = derived(newPresentationOptions, (options) => options.slides);

    enum GeneratorStatus {
        WAITING,
        GENERATING,
        ERROR,
        DONE
    }

    let selectedSuggestion;
    let suggestionModal: HTMLDialogElement;

    let status: GeneratorStatus = GeneratorStatus.WAITING;
    let topic: string;
    let language: string;
    let numberOfSlides: number;
    let speaker: string;
    let generator: string;
    let textGenerator: string;
    let generatorMessage: string = '';

    $: submitDisabled = !topic || topic.length < 3;

    async function generateNewTopic(event: Event) {
        event.preventDefault();
        event.stopPropagation();

        console.log("Generating topic");
        topic = await generateTopic();
        console.log(`Generated topic "${topic}"`);
    }

    async function selectSuggestion(event: Event) {
        event.preventDefault();
        event.stopPropagation();

        if (suggestionModal) {
            await getSuggestions();
            suggestionModal.showModal();
        }
    }

    function useSelection(event: Event) {
        event.preventDefault();
        event.stopPropagation();

        topic = selectedSuggestion;

        if(suggestionModal) {
            suggestionModal.close();
        }
    }

    function reset() {
        status = GeneratorStatus.WAITING;
    }

    function submit(event: Event) {
        event.preventDefault();
        event.stopPropagation();

        status = GeneratorStatus.GENERATING;

        createNewPresentation({
            imageGeneratorName: generator,
            textGeneratorName: textGenerator,
            language,
            numberOfSlides,
            speakerName: speaker,
            title: topic
        }).pipe(tap(msg => {
            console.log(msg);
        })).subscribe({
            next(message) {
                if (message.type === 'progress') {
                    generatorMessage = message.message ?? '';
                }
                if (message.type === 'error') {
                    generatorMessage = message.message ?? '';
                    status = GeneratorStatus.ERROR;
                }
                if (message.type === 'complete') {
                    status = GeneratorStatus.DONE;
                    window.location.href = message.url;
                }
            },
            error(err) {
                console.error(err);
                generatorMessage = 'technical error';
                status = GeneratorStatus.ERROR;
            },
            complete() {}
        });
    }
</script>

<div class="new-presentation-container">
    <h2>New Presentation</h2>
    <br>
    {#if status === GeneratorStatus.GENERATING}
        <div class="loading">
            <div class="pl pl-leapfrog"></div>
            <div class="loading-text">generating...</div>
        <div class="message">{generatorMessage}</div>
    </div>
    {:else if status === GeneratorStatus.ERROR}
        <div>
            Generation has failed<br/>
            <div class="error">{generatorMessage}</div>
            <button on:click={reset}>try again?</button>
        </div>
    {:else if status === GeneratorStatus.DONE}
        <div>
            Presentation is done, redirecting...
        </div>
    {:else}
        <dialog bind:this={suggestionModal}>
            <form on:submit={useSelection}>
                <select bind:value={selectedSuggestion}>
                    {#each $suggestions as suggestion}
                        <option value={suggestion.suggestion}>{suggestion.suggestion}</option>
                    {/each}
                </select>
                <input type='submit' value='use' />
            </form>
        </dialog>
        <form class="presentation-form">
            <label for="topic">Topic</label>
            <div class="input-two-buttons">
                <input class="split" type="text" id="topic" bind:value={topic} />
                <button class="split" on:click={generateNewTopic}>Generate topic</button>
                <button class="split" on:click={selectSuggestion}>Use a suggestion</button>
            </div>

            <label for="slide">Number of Slides</label>
            <select id="slide" bind:value={numberOfSlides}>
                {#each $slideOptions as slideOption}
                    <option value="{slideOption.value}">{slideOption.label}</option>
                {/each}
            </select>

            <label for="language">Language</label>
            <select id="language" bind:value={language}>
                {#each $languageOptions as languageOption}
                    <option value="{languageOption.value}">{languageOption.label}</option>
                {/each}
            </select>

            <label for="speaker">Speaker</label>
            <select id="speaker" bind:value={speaker}>
                {#each $speakerOptions as speakerOption}
                    <option value="{speakerOption.value}">{speakerOption.label}</option>
                {/each}
            </select>

            <label for="generator">Image Generator</label>
            <select id="generator" bind:value={generator}>
                {#each $generatorOptions as generatorOption}
                    <option value="{generatorOption.value}">{generatorOption.label}</option>
                {/each}
            </select>

            <label for="generator">Text Generator</label>
            <select id="textGenerator" bind:value={textGenerator}>
                {#each $textGeneratorOptions as textGeneratorOption}
                    <option value="{textGeneratorOption.value}">{textGeneratorOption.label}</option>
                {/each}
            </select>

            <input type="submit" value="Create Presentation" disabled="{submitDisabled}" on:click={submit}/>
        </form>
    {/if}
</div>

<style>
    .error {
        color: red;
        font-weight: bold;
    }

    .input-two-buttons {
        display: flex;
        flex-direction: row;
        justify-content: stretch;
        align-items: baseline;
    }

    input.split {
        flex-grow: 5;
    }

    button.split {
        flex-grow: 1;
        margin-left: 0.5%;
    }

    .new-presentation-container {
    }

    .presentation-form {
        display: grid;
        grid-template-columns: max-content 1fr;
        gap: 0.5rem;
    }

    .presentation-form label {
        text-align: left;
    }

    @media (max-width: 400px) {
        .presentation-form {
            grid-template-columns: 1fr;
        }

        .presentation-form label {
            text-align: left;
        }
    }

    .loading {
        display: flex;
        flex-direction: column;
        align-items: center;
    }

    /** Global preloader styles **/
    .pl, .pl:before, .pl:after {
        animation-duration: 2s;
        animation-timing-function: linear;
        animation-iteration-count: infinite;
    }
    .pl {
        margin-top: 5rem;
        margin-bottom: 2rem;
        position: relative;
        width: 6rem;
        height: 6rem;
    }
    .pl:before, .pl:after {
        background: currentColor;
        content: "";
        display: block;
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 50%;
        transform-origin: 50% 100%;
        clip-path: polygon(0 0,100% 0,50% 100%);
        -webkit-clip-path: polygon(0 0,100% 0,50% 100%);
    }

    /* Leap Frog */
    .pl-leapfrog:before, .pl-leapfrog:after {
        clip-path: polygon(0 0,100% 0,0 100%);
        -webkit-clip-path: polygon(0 0,100% 0,0 100%);
        top: 50%;
        left: 50%;
        width: 70.71%;
        height: 70.71%;
        transform-origin: 0 0;
    }
    .pl-leapfrog:before {
        animation-name: leapFrogA;
    }
    .pl-leapfrog:after {
        animation-name: leapFrogB;
        background: #a3a3a3;
    }
    @keyframes leapFrogA {
        from { transform: rotateZ(-135deg) rotateY(0deg) rotateX(0deg); z-index: 0 }
        12.5% { transform: rotateZ(-135deg) rotateY(-180deg) rotateX(0deg); z-index: 1 }
        25%, 50% { transform: rotateZ(-135deg) rotateY(-180deg) rotateX(-180deg); z-index: 0 }
        62.5% { transform: rotateZ(-135deg) rotateY(0deg) rotateX(-180deg); z-index: 1 }
        75%, to { transform: rotateZ(-135deg) rotateY(0deg) rotateX(-360deg); z-index: 0 }
    }
    @keyframes leapFrogB {
        from, 25% { transform: rotateZ(-45deg) rotateY(0deg) rotateX(0deg); z-index: 0 }
        37.5% { transform: rotateZ(-45deg) rotateY(-180deg) rotateX(0deg); z-index: 1 }
        50%, 75% { transform: rotateZ(-45deg) rotateY(-180deg) rotateX(-180deg); z-index: 0 }
        87.5% { transform: rotateZ(-45deg) rotateY(0deg) rotateX(-180deg); z-index: 1 }
        to { transform: rotateZ(-45deg) rotateY(0deg) rotateX(-360deg); z-index: 0 }
    }

    /** Media queries **/
    @media (prefers-color-scheme: dark) {
        .pl-leapfrog:after {
            background: #575757;
        }
    }
</style>
